import {
  ARQUEOS_REQUEST, ARQUEOS_SUCCESS, ARQUEOS_FAILURE,
  ARQUEOS_SALES_REQUEST, ARQUEOS_SALES_SUCCESS, ARQUEOS_SALES_FAILURE,
  ARQUEOS_GENERATED_SALES_REQUEST, ARQUEOS_GENERATED_SALES_SUCCESS, ARQUEOS_GENERATED_SALES_FAILURE,
  ARQUEOS_GENERATED_OPERATIONAL_REQUEST, ARQUEOS_GENERATED_OPERATIONAL_SUCCESS, ARQUEOS_GENERATED_OPERATIONAL_FAILURE,
  ARQUEOS_PERSISTED_SALES_REQUEST, ARQUEOS_PERSISTED_SALES_SUCCESS, ARQUEOS_PERSISTED_SALES_FAILURE,
  ARQUEOS_PERSISTED_OPERATIONAL_REQUEST, ARQUEOS_PERSISTED_OPERATIONAL_SUCCESS, ARQUEOS_PERSISTED_OPERATIONAL_FAILURE,
  ARQUEOS_GENERATE_RANGE_REQUEST, ARQUEOS_GENERATE_RANGE_SUCCESS, ARQUEOS_GENERATE_RANGE_FAILURE
} from '../actions/arqueos'

// All locker sizes share a single commission rate. If a locker size was not
// configured for the customer when the arqueo was generated, 0% is stored for
// that size, so its GMV stays in the take-rate denominator while it contributes
// no commission — dragging the take rate below the real rate. Recompute each
// row's CommissionAmount so every locker item (code starting with "LOCKER")
// uses the customer's locker rate (taken as the first non-zero locker rate
// present on the row, since all locker sizes share one rate). Non-locker items
// (TUMBONAS / FLOTADORES / FLOTADORESHALFDAY / FASTPASS) are NOT lockers and
// keep their own rate.
//
// REVENUE SHARE only (other models carry no commission); rows without the
// per-item breakdown are left untouched. Uses each row's OWN stored
// ItemsComissions, so historical take rates stay anchored to the rate that was
// billed then — only the missing-locker-size gaps get filled, not the rate.
//
// Runs BEFORE correctRefundCommission so the refund rescale (SalesTotal /
// ΣItemsPrices) operates on the locker-corrected commission. On a refund-free
// day the two compose to just this correction.
const correctLockerCommission = (rows) => {
  if (!Array.isArray(rows)) return rows
  return rows.map(row => {
    if (!row || row.BusinessModel !== 'REVENUE SHARE') return row
    const prices = row.ItemsPrices
    const comm = row.ItemsComissions
    if (!prices || !comm) return row
    const tax = Number(row.Tax) || 0
    // Standard rental lockers only. LOCKERX* are X-mode voucher lockers — a
    // separate family that can carry its own rate, so they keep it (same as
    // non-locker products) rather than inheriting the standard locker rate.
    const isLocker = (name) => name.startsWith('LOCKER') && !name.startsWith('LOCKERX')
    // reference locker rate = first configured (non-zero) standard-locker rate
    let lockerPct = 0
    for (const name of Object.keys(comm)) {
      if (isLocker(name) && Number(comm[name]) > 0) { lockerPct = Number(comm[name]); break }
    }
    let commission = 0
    for (const name of Object.keys(prices)) {
      const priceWithTax = Number(prices[name]) || 0
      const p = isLocker(name) ? lockerPct : (Number(comm[name]) || 0)
      const base = tax ? priceWithTax / (1 + tax / 100) : priceWithTax
      commission += base * (p / 100)
    }
    const corrected = Number(commission.toFixed(2))
    const salesTotal = Number(row.SalesTotal) || 0
    return {
      ...row,
      CommissionAmount: corrected,
      TakeRate: salesTotal > 0 ? Number((corrected / salesTotal).toFixed(4)) : row.TakeRate
    }
  })
}

// Devolutions / counter-invoices are stored as a sale with a NEGATIVE Amount but a
// POSITIVE Items[].price, so each refund inflates ItemsPrices (the commission base)
// by 2x its value while SalesTotal correctly nets it out. The arqueo's stored
// CommissionAmount is therefore computed on the inflated base, so the dashboard
// take rate (Σ CommissionAmount / Σ GMV_net) is wrong on any day with a refund.
//
// Fix it on read (no arqueo regeneration): rescale each row's CommissionAmount
// onto the real refund-netted sales base (SalesTotal / Σ ItemsPrices). Rows with
// no refund (Σ ItemsPrices == SalesTotal) are left untouched.
//
// Exact when a machine's sold sizes share one commission rate (the normal case);
// for genuinely mixed per-size rates it's a proportional approximation, since the
// stored row no longer says which size was refunded.
const correctRefundCommission = (rows) => {
  if (!Array.isArray(rows)) return rows
  return rows.map(row => {
    if (!row || !row.ItemsPrices) return row
    const itemsSum = Object.values(row.ItemsPrices).reduce((s, v) => s + (Number(v) || 0), 0)
    const salesTotal = Number(row.SalesTotal) || 0
    const commission = Number(row.CommissionAmount) || 0
    if (!commission || itemsSum <= 0 || Math.abs(salesTotal - itemsSum) < 0.005) return row
    const corrected = Number((commission * (salesTotal / itemsSum)).toFixed(2))
    return {
      ...row,
      CommissionAmount: corrected,
      TakeRate: salesTotal > 0 ? Number((corrected / salesTotal).toFixed(4)) : row.TakeRate
    }
  })
}

// Combined on-read corrections for sales arqueos: locker fallback first, then
// the refund rescale (order matters — the rescale must operate on the
// locker-corrected commission). Exported so views that consume the raw API
// response directly — e.g. the dashboard's last-year comparison fetch, which
// captures the thunk's response into local state instead of reading the Redux
// store — apply the SAME corrections and don't show an uncorrected LY take rate.
export const applySalesArqueoCorrections = (rows) =>
  correctRefundCommission(correctLockerCommission(rows))

const INITIAL_STATE = {
  arqueos: null,
  arqueo: null,
  arqueosNeedRefresh: false,
  error: null,
  loading: false,
  generatedSalesArqueos: null,
  generatedOperationalArqueos: null,
  generateRangeResult: null
}

const arqueosReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case ARQUEOS_REQUEST:
      return {
        ...state,
        arqueosNeedRefresh: false, error: null, loading: true
      }
    case ARQUEOS_SUCCESS:
      return {
        ...state,
        arqueosNeedRefresh: false, arqueos: action.response, error: null, loading: false
      }
    case ARQUEOS_FAILURE:
      return {
        ...state,
        error: action.error, loading: false
      }
    case ARQUEOS_SALES_REQUEST:
      return {
        ...state,
        arqueosNeedRefresh: false, error: null, loading: true
      }
    case ARQUEOS_SALES_SUCCESS:
      return {
        ...state,
        arqueosNeedRefresh: false, arqueos: action.response, error: null, loading: false
      }
    case ARQUEOS_SALES_FAILURE:
      return {
        ...state,
        error: action.error, loading: false
      }
    case ARQUEOS_GENERATED_SALES_REQUEST:
      return { ...state, generatedSalesArqueos: null, loading: true, error: null }
    case ARQUEOS_GENERATED_SALES_SUCCESS:
      return { ...state, generatedSalesArqueos: applySalesArqueoCorrections(action.response), loading: false, error: null }
    case ARQUEOS_GENERATED_SALES_FAILURE:
      return { ...state, generatedSalesArqueos: null, loading: false, error: action.error }

    // persisted sales (reads new collection only)
    case ARQUEOS_PERSISTED_SALES_REQUEST:
      return { ...state, generatedSalesArqueos: null, loading: true, error: null }
    case ARQUEOS_PERSISTED_SALES_SUCCESS:
      return { ...state, generatedSalesArqueos: applySalesArqueoCorrections(action.response), loading: false, error: null }
    case ARQUEOS_PERSISTED_SALES_FAILURE:
      return { ...state, generatedSalesArqueos: null, loading: false, error: action.error }

    case ARQUEOS_GENERATED_OPERATIONAL_REQUEST:
      return { ...state, generatedOperationalArqueos: null, loading: true, error: null }
    case ARQUEOS_GENERATED_OPERATIONAL_SUCCESS:
      return { ...state, generatedOperationalArqueos: action.response, loading: false, error: null }
    case ARQUEOS_GENERATED_OPERATIONAL_FAILURE:
      return { ...state, generatedOperationalArqueos: null, loading: false, error: action.error }

    // persisted operational (reads new collection only)
    case ARQUEOS_PERSISTED_OPERATIONAL_REQUEST:
      return { ...state, generatedOperationalArqueos: null, loading: true, error: null }
    case ARQUEOS_PERSISTED_OPERATIONAL_SUCCESS:
      return { ...state, generatedOperationalArqueos: action.response, loading: false, error: null }
    case ARQUEOS_PERSISTED_OPERATIONAL_FAILURE:
      return { ...state, generatedOperationalArqueos: null, loading: false, error: action.error }

    case ARQUEOS_GENERATE_RANGE_REQUEST:
      return { ...state, generateRangeResult: null, loading: true, error: null }
    case ARQUEOS_GENERATE_RANGE_SUCCESS:
      return { ...state, generateRangeResult: action.response, loading: false, error: null }
    case ARQUEOS_GENERATE_RANGE_FAILURE:
      return { ...state, generateRangeResult: null, loading: false, error: action.error }
    default:
      return state
  }
}

export default arqueosReducer

